import styles from "./map.module.css";
import mapboxgl from "mapbox-gl";
import { useCallback, useRef, useEffect, MouseEvent, useMemo } from "react";
import { drawMapElements, MapDrawingConfig } from "../utils/canvas-drawing";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { MapUIOverlay } from "./overlay/MapUIOverlay";
import { MapElement } from "../utils/canvas-drawing";
import { useElementDragging } from "./use-element-dragging";
import { isCoordinateInsideBounds } from "PosterEditor/utils/coordinate-utils";
import {
  mapboxLngLatBoundsFromCoordinateBounds,
  defaultLngLatBounds,
} from "PosterEditor/hooks/use-coordinates";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";

import { useEditorUI } from "PosterEditor/use-editor-ui";
import { CoordinateBounds } from "PosterEditor/hooks/use-coordinates";
import { Gallery } from "PosterEditor/model/poster-project";
import {
  findViewportUnderPointer,
  calculateContentRect,
  calculateContentSlack,
  canPanContent,
  MIN_CONTENT_SCALE,
} from "PosterEditor/utils/canvas-drawing/canvas-drawing-gallery";
import { Rect, Point } from "PosterEditor/model/poster-types";
import { constrainOffset } from "./use-element-dragging";
import { findViewportInCells, isImageLoaded } from "PosterEditor/utils/gallery-utils";

interface FitBoundsPadding {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Calculates asymmetric padding for fitBounds that pushes map content
 * away from any gallery overlay, positioning it in the visible area.
 *
 * Accounts for:
 * - gallery.gap: the gap between cells
 * - gallery.gapAroundEdges: whether gap is applied around gallery edges
 * - gallery.gapTowardMap: whether gap is applied toward the map area
 */
function calculateGalleryAwarePadding(
  gallery: Gallery | undefined,
  canvasWidth: number,
  canvasHeight: number,
  basePadding: number = 20,
): FitBoundsPadding {
  if (
    !gallery ||
    !gallery.isVisible ||
    canvasWidth === 0 ||
    canvasHeight === 0
  ) {
    return {
      top: basePadding,
      bottom: basePadding,
      left: basePadding,
      right: basePadding,
    };
  }

  // Calculate gallery bounds in pixels
  const galleryLeft = gallery.position.x * canvasWidth;
  const galleryTop = gallery.position.y * canvasHeight;
  const galleryRight = (gallery.position.x + gallery.size.width) * canvasWidth;
  const galleryBottom =
    (gallery.position.y + gallery.size.height) * canvasHeight;

  // Calculate gaps from each edge to the gallery
  const gapLeft = galleryLeft;
  const gapRight = canvasWidth - galleryRight;
  const gapTop = galleryTop;
  const gapBottom = canvasHeight - galleryBottom;

  // Find which edge has the largest gap (that's where the map content should go)
  const gaps = [
    { edge: "left" as const, gap: gapLeft },
    { edge: "right" as const, gap: gapRight },
    { edge: "top" as const, gap: gapTop },
    { edge: "bottom" as const, gap: gapBottom },
  ];

  const largestGap = gaps.reduce((max, curr) =>
    curr.gap > max.gap ? curr : max,
  );

  // Start with base padding on all sides
  const padding: FitBoundsPadding = {
    top: basePadding,
    bottom: basePadding,
    left: basePadding,
    right: basePadding,
  };

  // Calculate extra padding for the gallery area
  // The gap toward map adds extra visual space between gallery content and map
  const gapTowardMapPadding = gallery.gapTowardMap ? gallery.gap : 0;

  // Add extra padding on the side opposite to the largest gap to push content toward visible area
  switch (largestGap.edge) {
    case "left":
      // Largest gap is on left, so gallery is on right - add right padding
      padding.right = canvasWidth - gapLeft + basePadding + gapTowardMapPadding;
      break;
    case "right":
      // Largest gap is on right, so gallery is on left - add left padding
      padding.left = canvasWidth - gapRight + basePadding + gapTowardMapPadding;
      break;
    case "top":
      // Largest gap is on top, so gallery is on bottom - add bottom padding
      padding.bottom =
        canvasHeight - gapTop + basePadding + gapTowardMapPadding;
      break;
    case "bottom":
      // Largest gap is on bottom, so gallery is on top - add top padding
      padding.top =
        canvasHeight - gapBottom + basePadding + gapTowardMapPadding;
      break;
  }

  return padding;
}

interface MapProps {
  onMapLoad: (mapReference: any) => void;
  onStaticImageURLFetched: (url: string) => void;
  onClick: () => void;
  onMove: () => void;
  onChangeElementUnderCursor: (element: MapElement | undefined) => void;
  onShowElementEditor: (show: boolean) => void;
  onLoadCanvas: (canvas: any) => void;
  isMobile?: boolean;
}

export const Map = (props: MapProps) => {
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ?? "";
  mapboxgl.accessToken = accessToken;
  const {
    project,
    setCoordinateBounds: saveBoundsToProject,
    gpsSource,
    isTemplateEditing,
    imageCache,
    styledPointsRequiringBoundsUpdate,
    clearStyledPointsRequiringBoundsUpdate,
    updateViewportContent,
  } = usePosterProject();
  const {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    pointerLocation,
    setPointerLocation,
    isDragging,
    isPanningViewport,
    setDraggingElement,
    elementUnderCursor,
    setElementUnderCursor,
    setSnapLines,
    setMapRef,
  } = useElementDragging();
  const { editorUIState, setMapSizeInPX, setFocusedViewport, setPendingPickerViewport, setEditingElement, setSelectedViewportId } = useEditorUI();
  const mapContainer = useRef<any>(null);
  const map = useRef<any>(null);
  const canvasOverlay = useRef<any>(null);
  // Track whether we're waiting for initial GPS fit - prevents moveend from saving
  // default bounds before GPS data loads (race condition fix for Strava flow)
  const pendingInitialGPSFitRef = useRef<boolean>(false);
  // Ref to store the viewport upload trigger function from MapUIOverlay
  // (iOS requires file input clicks to happen directly in the user gesture call stack)
  const viewportUploadTriggerRef = useRef<((viewportId: string, viewportRect?: Rect, zoomAfterUpload?: boolean) => void) | null>(null);

  // Mobile touch panning refs - must be refs (not local variables) to persist across
  // effect re-runs when project.gallery changes from updateViewportContent calls
  const mobilePanStateRef = useRef<{
    isPanning: boolean;
    panStartPoint: { x: number; y: number } | null;
    panStartOffset: { x: number; y: number } | null;
    panViewportRect: { x: number; y: number; width: number; height: number } | null;
    panImageSize: { width: number; height: number } | null;
  }>({
    isPanning: false,
    panStartPoint: null,
    panStartOffset: null,
    panViewportRect: null,
    panImageSize: null,
  });
  const {
    isLoading: sizePresetsLoading,
    cssAspectRatio,
    incrementSizeInPX,
    screenSizeFromProject,
  } = useSizePresets();

  const listenToMapMoveEvents = () => {
    map.current.on("move", () => {
      props.onMove();
    });

    // Only save bounds when movement is complete to avoid triggering re-renders on every frame
    map.current.on("moveend", () => {
      // Skip saving bounds if we're waiting for initial GPS fit
      // This prevents the race condition where map initializes with default bounds
      // before GPS data loads asynchronously (e.g., Strava flow)
      if (pendingInitialGPSFitRef.current) {
        console.log(
          "[MAP MOVEEND] Skipping bounds save - waiting for initial GPS fit",
        );
        return;
      }

      const bounds = map.current.getBounds();
      const northeast = bounds.getNorthEast();
      const southwest = bounds.getSouthWest();

      const coordinateBounds: CoordinateBounds = {
        northEast: {
          lat: northeast.lat,
          lng: northeast.lng,
        },
        southWest: {
          lat: southwest.lat,
          lng: southwest.lng,
        },
      };
      saveBoundsToProject(coordinateBounds);
    });
  };

  useEffect(() => {
    if (!map.current) {
      return;
    }
    if (editorUIState.editingElement) {
      map.current.keyboard.disable();
    } else {
      map.current.keyboard.enable();
    }
  }, [editorUIState.editingElement]);

  useEffect(() => {
    props.onLoadCanvas(canvasOverlay.current);
  }, [canvasOverlay]);

  useEffect(() => {
    if (map.current) {
      const zoom = map.current.getZoom();
      if (zoom) {
        map.current.easeTo({
          zoom: zoom + 0.4,
        });
      }
    }
  }, [editorUIState.mapZoomInCounter]);

  useEffect(() => {
    if (map.current) {
      const zoom = map.current.getZoom();
      if (zoom) {
        map.current.easeTo({
          zoom: zoom - 0.4,
        });
      }
    }
  }, [editorUIState.mapZoomOutCounter]);

  // CONFIGURE THE MAP - creates map on mount and recreates on style change
  useEffect(() => {
    // Clean up old map instance before creating new one
    if (map.current) {
      map.current.remove();
    }
    createMap();
  }, [project.mapStyle]);

  useEffect(() => {
    map.current.resize();
    drawContent();
  }, [project.borderStyle, project.sizeOption]);

  const createMap = () => {
    const bounds = project.bounds
      ? mapboxLngLatBoundsFromCoordinateBounds(project.bounds)
      : defaultLngLatBounds;

    // If project has GPS sources but no saved bounds, we're waiting for GPS to load
    // Set flag to prevent moveend from saving default bounds before GPS fit
    if (
      !project.bounds &&
      project.gpsSourceIDs &&
      project.gpsSourceIDs.length > 0
    ) {
      pendingInitialGPSFitRef.current = true;
      console.log(
        "[MAP INIT] Pending initial GPS fit - will skip moveend bounds save",
      );
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: project.mapStyle,
      bounds: bounds,
    });
    map.current.dragRotate.disable(); // Disables right-click drag rotation
    map.current.touchZoomRotate.disableRotation(); // Disables two-finger rotation on touch devices
    map.current.keyboard.disableRotation(); // Disables keyboard-based rotation (e.g. arrow keys + ctrl)
    map.current.doubleClickZoom.disable(); // (Optional) disables double-click zoom if undesired
    map.current.pitchEnabled = false;
    listenToMapMoveEvents();
    props.onMapLoad(map);

    setMapRef(map);

    if (map.current && project.bounds) {
      const bounds = project.bounds
        ? mapboxLngLatBoundsFromCoordinateBounds(project.bounds)
        : defaultLngLatBounds;
      map.current.fitBounds(bounds, {
        padding: 0, // Optional: padding around the bounds
        duration: 0.3, // Optional: disable animation if needed
      });

      //map.current.dragPan.disable();
    }
  };

  // Helper to convert screen coordinates to poster coordinates
  // Uses getBoundingClientRect() which automatically accounts for all CSS transforms
  // (including focus transform from PosterStage.tsx)
  const screenToPosterCoords = useCallback((screenX: number, screenY: number) => {
    const container = mapContainer.current;
    if (!container) return { x: screenX, y: screenY };

    // getBoundingClientRect() returns the visual position/size AFTER all CSS transforms
    const rect = container.getBoundingClientRect();

    // offsetWidth/Height are the actual canvas dimensions (unscaled)
    // rect.width/height are the visual dimensions (after CSS transform scaling)
    // The ratio gives us the inverse scale factor
    const scaleX = container.offsetWidth / rect.width;
    const scaleY = container.offsetHeight / rect.height;

    const result = {
      x: (screenX - rect.left) * scaleX,
      y: (screenY - rect.top) * scaleY,
    };

    return result;
  }, []);

  // HANDLE MOUSE/TOUCH POINTER TRACKING
  // iPad in desktop mode (≥1024px) sends touch events, so we need touch listeners too
  // Note: Mobile mode now uses same touch interactions as desktop mode
  useEffect(() => {
    const container = mapContainer.current;
    if (!container) return;

    const handleMouseMove = (event: globalThis.MouseEvent) => {
      const coords = screenToPosterCoords(event.clientX, event.clientY);
      setPointerLocation(coords);
    };

    const handleMouseLeave = () => {
      setPointerLocation(undefined);
    };

    // Touch handlers for pointer tracking (touch interactions handled separately)
    const handleTouchMoveForTracking = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      const coords = screenToPosterCoords(touch.clientX, touch.clientY);
      setPointerLocation(coords);
    };

    const handleTouchStartForTracking = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      const coords = screenToPosterCoords(touch.clientX, touch.clientY);
      setPointerLocation(coords);
    };

    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseleave", handleMouseLeave);
    container.addEventListener("touchstart", handleTouchStartForTracking);
    container.addEventListener("touchmove", handleTouchMoveForTracking);

    return () => {
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseleave", handleMouseLeave);
      container.removeEventListener("touchstart", handleTouchStartForTracking);
      container.removeEventListener("touchmove", handleTouchMoveForTracking);
    };
  }, [setPointerLocation, screenToPosterCoords]);

  // HANDLE TOUCH EVENTS (for all touch devices)
  // Uses native event listeners with { passive: false } so we can call preventDefault()
  // React's synthetic touch events are passive by default and cannot be prevented
  //
  // Key insight: Touch events need SYNCHRONOUS hit-testing because:
  // 1. setPointerLocation(coords) is async (React state update)
  // 2. elementUnderCursor is computed during render based on pointerLocation
  // 3. So when touchstart fires, the old elementUnderCursor is stale
  //
  // Solution: Call drawContent(coords) synchronously which:
  // - Computes the element under the touch point immediately
  // - Updates elementUnderCursorRef for synchronous access
  // - Updates state for the next render
  useEffect(() => {
    const container = mapContainer.current;
    if (!container) return;

    // Track dragging state in refs for access in native event handlers
    const draggingRef = { current: false };

    const handleTouchStart = (e: TouchEvent) => {
      // Multi-touch gesture (pinch zoom) - ensure map touch handlers are enabled
      if (e.touches.length !== 1) {
        if (map.current) {
          map.current.dragPan.enable();
          map.current.touchZoomRotate.enable();
          map.current.touchZoomRotate.disableRotation();
        }
        draggingRef.current = false;
        return;
      }

      const touch = e.touches[0];
      const coords = screenToPosterCoords(touch.clientX, touch.clientY);
      setPointerLocation(coords);

      // Synchronously compute what element is under the touch point
      // This updates elementUnderCursorRef immediately
      drawContent(coords);

      const touchedElement = elementUnderCursorRef.current;

      // Handle empty gallery viewports FIRST, before preventDefault()
      // iOS Safari requires file input clicks to happen without any preventDefault() interference
      if (touchedElement?.type === "gallery" && (touchedElement as any).viewportId) {
        const viewportId = (touchedElement as any).viewportId;
        const viewportRect = touchedElement.rect;

        // Check if viewport is empty (no image) - check both canvas state and project data
        const hasChangeImageAction = !!(touchedElement as any).changeImageAction;
        const viewport = project.gallery?.content ? findViewportInCells(project.gallery.content, viewportId) : undefined;
        const isEmptyViewport = hasChangeImageAction || (viewport && !viewport.imageID);

        if (isEmptyViewport) {
          // For empty viewports, open image picker directly in user gesture call stack
          // IMPORTANT: Do NOT call e.preventDefault() - iOS needs the native event chain intact
          if (viewportUploadTriggerRef.current) {
            viewportUploadTriggerRef.current(viewportId, viewportRect, true);
          } else {
            // Fallback to state-based approach if trigger not available yet
            setPendingPickerViewport(viewportId, viewportRect);
          }
          return;
        }
      }

      // Handle element interactions - but DON'T preventDefault or disable zoom yet
      // We defer that to touchmove so pinch-to-zoom can still work
      if (touchedElement) {
        // Handle populated gallery viewports
        if (touchedElement.type === "gallery" && (touchedElement as any).viewportId) {
          const viewportId = (touchedElement as any).viewportId;
          const viewportRect = touchedElement.rect;

          // Check if this viewport is already selected (ready for panning)
          const isAlreadySelected = editorUIState.selectedViewportId === viewportId;

          if (!isAlreadySelected) {
            // First touch on viewport - focus it and select it for panning
            // Clear any selected label to ensure mutual exclusion
            setEditingElement(undefined);
            setFocusedViewport(viewportId, viewportRect);
            setSelectedViewportId(viewportId);
            return;
          }

          // Viewport is already selected - mark as ready to pan
          // Actual disable of map handlers happens in touchmove
          draggingRef.current = true;
          handleMouseDown(true);
          return;
        }

        // LHS-257: On mobile, don't select or drag static labels, backdrops, or map labels
        const isNonInteractiveOnMobile =
          props.isMobile &&
          (touchedElement.type === "staticLabel" ||
            touchedElement.type === "backdrop" ||
            touchedElement.type === "mapLabel");

        if (isNonInteractiveOnMobile) {
          // Don't select or drag these elements on mobile - treat as empty space tap
          return;
        }

        // Mark that we're starting to drag an element
        draggingRef.current = true;

        // Select the element by setting it as editing (replaces onClick which won't fire due to preventDefault)
        // Convert MapElement to ProjectElement format - only for non-gallery elements
        if (touchedElement.element && touchedElement.type !== "gallery") {
          // Clear any selected viewport to ensure mutual exclusion
          setFocusedViewport(undefined, undefined);
          setSelectedViewportId(undefined);

          const element = touchedElement.element as any;
          const projectElement = {
            type: touchedElement.type,
            id: element.id,
            point: { x: touchedElement.rect.x, y: touchedElement.rect.y },
            mapLabel: touchedElement.type === "mapLabel" ? element : undefined,
            staticLabel: touchedElement.type === "staticLabel" ? element : undefined,
            backdrop: touchedElement.type === "backdrop" ? element : undefined,
          };
          setEditingElement(projectElement);
        }
      } else {
        // Touched empty space - deselect
        setEditingElement(undefined);
        setFocusedViewport(undefined, undefined);
        setSelectedViewportId(undefined);
      }

      // Pass true to force eligibility for touch (since setEditingElement is async)
      handleMouseDown(!!touchedElement);
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Multi-touch - let pinch-to-zoom work
      if (e.touches.length !== 1) {
        // If we were dragging, stop and re-enable map handlers
        if (draggingRef.current && map.current) {
          map.current.dragPan.enable();
          map.current.touchZoomRotate.enable();
          map.current.touchZoomRotate.disableRotation();
          draggingRef.current = false;
        }
        return;
      }

      const touch = e.touches[0];
      const coords = screenToPosterCoords(touch.clientX, touch.clientY);
      setPointerLocation(coords);

      // Synchronously update element under cursor
      drawContent(coords);

      // Only disable map handlers and preventDefault when actually dragging with single finger
      // This allows pinch-to-zoom to start before we intercept
      if (draggingRef.current || isDragging || isPanningViewport) {
        e.preventDefault();
        // Disable map handlers on first move while dragging
        if (map.current) {
          map.current.dragPan.disable();
          map.current.touchZoomRotate.disable();
        }
      }

      handleMouseMove();
    };

    const handleTouchEnd = () => {
      // Re-enable Mapbox's touch handlers when dragging or panning ends
      if ((draggingRef.current || isPanningViewport) && map.current) {
        map.current.dragPan.enable();
        map.current.touchZoomRotate.enable();
        map.current.touchZoomRotate.disableRotation();
      }
      draggingRef.current = false;
      handleMouseUp();
    };

    // Use capture phase to ensure we get the event before Mapbox's handlers
    container.addEventListener('touchstart', handleTouchStart, { passive: false, capture: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    container.addEventListener('touchend', handleTouchEnd, { capture: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart, { capture: true });
      container.removeEventListener('touchmove', handleTouchMove, { capture: true });
      container.removeEventListener('touchend', handleTouchEnd, { capture: true });
    };
  }, [screenToPosterCoords, setPointerLocation, handleMouseDown, handleMouseMove, handleMouseUp, isDragging, isPanningViewport, setEditingElement, setFocusedViewport, setPendingPickerViewport, setSelectedViewportId, project.gallery, editorUIState.selectedViewportId, props.isMobile]);

  // HANDLE MOBILE TAP TO FOCUS VIEWPORT
  // DISABLED: Mobile now uses same touch interactions as desktop mode
  useEffect(() => {
    return; // Disabled - mobile uses desktop touch handlers now

    const container = mapContainer.current;
    if (!container) return;

    let touchStartPoint: { x: number; y: number } | null = null;
    let touchStartTime: number = 0;

    const handleTouchStart = (e: TouchEvent) => {
      // Only handle single-finger taps
      if (e.touches.length !== 1) {
        touchStartPoint = null;
        return;
      }

      const touch = e.touches[0];
      touchStartPoint = screenToPosterCoords(touch.clientX, touch.clientY);
      touchStartTime = Date.now();
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartPoint) return;

      // Check if this was a tap (short duration, minimal movement)
      const touchDuration = Date.now() - touchStartTime;
      if (touchDuration > 200) {
        touchStartPoint = null;
        return;
      }

      // Get the end position
      const touch = e.changedTouches[0];
      const endPoint = screenToPosterCoords(touch.clientX, touch.clientY);

      // Check movement distance
      const dx = endPoint.x - touchStartPoint.x;
      const dy = endPoint.y - touchStartPoint.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > 10) {
        touchStartPoint = null;
        return;
      }

      // This is a valid tap - perform hit detection
      const gallery = project.gallery;

      // If viewport is already focused, check if tap is inside it
      const focusedRect = editorUIState.focusedViewportRect;
      if (editorUIState.focusedViewportId && focusedRect) {
        const isInsideFocusedViewport =
          endPoint.x >= focusedRect.x &&
          endPoint.x <= focusedRect.x + focusedRect.width &&
          endPoint.y >= focusedRect.y &&
          endPoint.y <= focusedRect.y + focusedRect.height;

        if (!isInsideFocusedViewport) {
          // Tap outside focused viewport - exit focus mode
          setFocusedViewport(undefined, undefined);
        }
        // If inside, do nothing for now (panning handled separately)
        touchStartPoint = null;
        return;
      }

      if (!gallery || !gallery.isVisible) {
        touchStartPoint = null;
        return;
      }

      const canvasWidth = container.offsetWidth;
      const canvasHeight = container.offsetHeight;

      // Calculate container rect from gallery position/size
      const containerRect: Rect = {
        x: gallery.position.x * canvasWidth,
        y: gallery.position.y * canvasHeight,
        width: gallery.size.width * canvasWidth,
        height: gallery.size.height * canvasHeight,
      };

      // Calculate content rect with gap options applied
      const contentRect = calculateContentRect(containerRect, gallery);

      // Find viewport under tap point
      const viewportResult = findViewportUnderPointer(
        gallery.content,
        contentRect,
        gallery.gap,
        endPoint,
      );

      if (viewportResult) {
        // Check if viewport is empty (no image)
        const viewport = findViewportInCells(gallery.content, viewportResult.viewportId);
        const isEmptyViewport = viewport && !viewport.imageID;

        if (isEmptyViewport) {
          // LHS-241: For empty viewports, skip zoom and open picker directly
          setPendingPickerViewport(viewportResult.viewportId, viewportResult.rect);
        } else {
          // For populated viewports, zoom in as usual
          setFocusedViewport(viewportResult.viewportId, viewportResult.rect);
        }
      }

      touchStartPoint = null;
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    props.isMobile,
    project.gallery,
    setFocusedViewport,
    setPendingPickerViewport,
    editorUIState.focusedViewportId,
    editorUIState.focusedViewportRect,
  ]);

  // HANDLE MOBILE TOUCH PANNING WHEN VIEWPORT IS FOCUSED
  // DISABLED: Mobile now uses same touch interactions as desktop mode
  useEffect(() => {
    return; // Disabled - mobile uses desktop touch handlers now

    const container = mapContainer.current;
    if (!container) return;

    // Use ref for panning state so it persists across effect re-runs
    const panState = mobilePanStateRef.current;

    const handleTouchStart = (e: TouchEvent) => {
      // Reset panning state if multi-touch detected
      if (e.touches.length !== 1) {
        panState.isPanning = false;
        panState.panStartPoint = null;
        panState.panStartOffset = null;
        panState.panViewportRect = null;
        panState.panImageSize = null;
        return;
      }

      const focusedViewportRect = editorUIState.focusedViewportRect;
      if (!focusedViewportRect) return;

      const touch = e.touches[0];

      // Convert screen coordinates to poster coordinates using inverse transform
      const touchPoint = screenToPosterCoords(touch.clientX, touch.clientY);

      // Check if touch is inside the focused viewport
      const isInsideViewport =
        touchPoint.x >= focusedViewportRect.x &&
        touchPoint.x <= focusedViewportRect.x + focusedViewportRect.width &&
        touchPoint.y >= focusedViewportRect.y &&
        touchPoint.y <= focusedViewportRect.y + focusedViewportRect.height;

      if (!isInsideViewport) return;

      // Get viewport and check if it has an image that can pan
      if (!project.gallery?.content) return;
      const viewport = findViewportInCells(project.gallery.content, editorUIState.focusedViewportId!);
      if (!viewport?.imageID) return;

      const imageElement = imageCache.get(`gallery-${viewport.imageID}`);
      if (!imageElement || !isImageLoaded(imageElement)) return;

      const scale = viewport.contentScale ?? 1.0;
      if (!canPanContent(
        imageElement.naturalWidth,
        imageElement.naturalHeight,
        focusedViewportRect.width,
        focusedViewportRect.height,
        scale
      )) return;

      // Start panning - capture all state needed for the gesture
      panState.isPanning = true;
      panState.panStartPoint = touchPoint;
      panState.panStartOffset = viewport.contentOffset ?? { x: 0, y: 0 };
      panState.panViewportRect = { ...focusedViewportRect };
      panState.panImageSize = { width: imageElement.naturalWidth, height: imageElement.naturalHeight };

      // Disable Mapbox's touch handling while we pan the image
      if (map.current) {
        map.current.dragPan.disable();
        map.current.touchZoomRotate.disable();
      }

      // Prevent default to stop map from panning
      e.preventDefault();
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!panState.isPanning || !panState.panStartPoint || !panState.panStartOffset || !panState.panViewportRect || !panState.panImageSize) return;

      // Reset if multi-touch detected during pan
      if (e.touches.length !== 1) {
        // Re-enable Mapbox touch handling
        if (map.current) {
          map.current.dragPan.enable();
          map.current.touchZoomRotate.enable();
          map.current.touchZoomRotate.disableRotation();
        }
        panState.isPanning = false;
        panState.panStartPoint = null;
        panState.panStartOffset = null;
        panState.panViewportRect = null;
        panState.panImageSize = null;
        return;
      }

      const touch = e.touches[0];

      // Convert screen coordinates to poster coordinates using inverse transform
      const currentPoint = screenToPosterCoords(touch.clientX, touch.clientY);

      // Calculate pointer delta in poster pixels
      const deltaX = currentPoint.x - panState.panStartPoint.x;
      const deltaY = currentPoint.y - panState.panStartPoint.y;

      console.log('[Touch Pan Debug] Raw delta:', { deltaX, deltaY });
      console.log('[Touch Pan Debug] panStartPoint:', panState.panStartPoint);
      console.log('[Touch Pan Debug] currentPoint:', currentPoint);

      if (!project.gallery?.content) return;
      const viewport = findViewportInCells(project.gallery.content, editorUIState.focusedViewportId!);
      if (!viewport?.imageID) return;

      const currentScale = viewport.contentScale ?? MIN_CONTENT_SCALE;

      // Calculate slack using the shared utility function
      const currentSlack = calculateContentSlack(
        panState.panImageSize.width,
        panState.panImageSize.height,
        panState.panViewportRect.width,
        panState.panViewportRect.height,
        currentScale
      );

      // Calculate base source dimensions (what portion of image fills viewport at scale 1.0)
      // This matches the logic in use-element-dragging.tsx:463-475
      const viewportAspect = panState.panViewportRect.width / panState.panViewportRect.height;
      const imageAspect = panState.panImageSize.width / panState.panImageSize.height;

      let baseSrcWidth: number;
      let baseSrcHeight: number;
      if (viewportAspect > imageAspect) {
        baseSrcWidth = panState.panImageSize.width;
        baseSrcHeight = panState.panImageSize.width / viewportAspect;
      } else {
        baseSrcWidth = panState.panImageSize.height * viewportAspect;
        baseSrcHeight = panState.panImageSize.height;
      }

      // Calculate pannable distance in poster pixels for the full offset range [−0.5, 0.5]
      // This is the key formula: slackX (in image pixels) * screen scale factor
      const pannableScreenX =
        (currentSlack.slackX * panState.panViewportRect.width * currentScale) / baseSrcWidth;
      const pannableScreenY =
        (currentSlack.slackY * panState.panViewportRect.height * currentScale) / baseSrcHeight;

      console.log('[Touch Pan Debug] Calculation inputs:', {
        slackX: currentSlack.slackX,
        slackY: currentSlack.slackY,
        viewportWidth: panState.panViewportRect.width,
        viewportHeight: panState.panViewportRect.height,
        currentScale,
        baseSrcWidth,
        baseSrcHeight,
        imageWidth: panState.panImageSize.width,
        imageHeight: panState.panImageSize.height,
      });
      console.log('[Touch Pan Debug] pannableScreen:', { pannableScreenX, pannableScreenY });

      // Convert screen delta to offset delta
      // Offset range is 1.0, so: offsetDelta = screenDelta / pannableScreen
      // Negative because dragging right should show more of right side (decrease offset)
      const offsetDeltaX = pannableScreenX > 0.001 ? -deltaX / pannableScreenX : 0;
      const offsetDeltaY = pannableScreenY > 0.001 ? -deltaY / pannableScreenY : 0;

      console.log('[Touch Pan Debug] offsetDelta:', { offsetDeltaX, offsetDeltaY });

      // Calculate new offset
      const newOffset = {
        x: panState.panStartOffset.x + offsetDeltaX,
        y: panState.panStartOffset.y + offsetDeltaY,
      };

      // Constrain offset to valid bounds using the shared utility function
      const constrainedOffset = constrainOffset(
        newOffset,
        currentSlack.slackX,
        currentSlack.slackY
      );

      console.log('[Touch Pan Debug] Final offset:', { panStartOffset: panState.panStartOffset, newOffset, constrainedOffset });

      updateViewportContent(editorUIState.focusedViewportId!, constrainedOffset, currentScale);

      e.preventDefault();
    };

    const handleTouchEnd = () => {
      // Re-enable Mapbox touch handling when panning ends
      if (panState.isPanning && map.current) {
        map.current.dragPan.enable();
        map.current.touchZoomRotate.enable();
        // Re-disable rotation (we always want rotation disabled)
        map.current.touchZoomRotate.disableRotation();
      }
      panState.isPanning = false;
      panState.panStartPoint = null;
      panState.panStartOffset = null;
      panState.panViewportRect = null;
      panState.panImageSize = null;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
      // Re-enable Mapbox touch handling on cleanup
      if (map.current) {
        map.current.dragPan.enable();
        map.current.touchZoomRotate.enable();
        map.current.touchZoomRotate.disableRotation();
      }
    };
  }, [props.isMobile, editorUIState.focusedViewportId, editorUIState.focusedViewportRect, project.gallery, updateViewportContent, imageCache, screenToPosterCoords]);

  // CENTER THE MAP ON LOADING COORDINATES
  useEffect(() => {
    let coords = gpsSource?.allCoordinates ?? [];
    let newStylesPointsRequireBoundsUpdate =
      styledPointsRequiringBoundsUpdate.length > 0;

    let styledPointCoords = project.styledPoints.map(
      (point) => point.coordinate,
    );

    if (project.bounds) {
      const anyPointOutsideBounds = project.styledPoints.some((point) => {
        return !isCoordinateInsideBounds(point.coordinate, project.bounds!);
      });
      if (!anyPointOutsideBounds) {
        newStylesPointsRequireBoundsUpdate = false;
        clearStyledPointsRequiringBoundsUpdate();
      }
    }

    coords = coords.concat(styledPointCoords);

    // Check if GPS coordinates are outside current bounds (indicates fresh GPS load needing refit)
    const gpsCoords = gpsSource?.allCoordinates ?? [];
    const hasGPSOutsideBounds =
      project.bounds &&
      gpsCoords.length > 0 &&
      !gpsCoords.some((coord) =>
        isCoordinateInsideBounds(coord, project.bounds!),
      );

    // DEBUG: Trace auto-focus logic
    console.log("[MAP AUTO-FOCUS DEBUG]", {
      gpsCoordCount: gpsCoords.length,
      projectBounds: project.bounds,
      hasGPSOutsideBounds,
      newStylesPointsRequireBoundsUpdate,
      styledPointsCount: project.styledPoints.length,
      totalCoordsCount: coords.length,
      pendingInitialGPSFit: pendingInitialGPSFitRef.current,
    });

    // Check if we're waiting for initial GPS fit (Strava flow race condition fix)
    const isPendingGPSFit = pendingInitialGPSFitRef.current;

    if (
      !newStylesPointsRequireBoundsUpdate &&
      project.bounds &&
      !hasGPSOutsideBounds &&
      !isPendingGPSFit
    ) {
      console.log("[MAP AUTO-FOCUS] BRANCH 1: Using existing project.bounds");
      const bounds = mapboxLngLatBoundsFromCoordinateBounds(project.bounds);
      map.current.fitBounds(bounds, {
        padding: 0,
        duration: 1000,
      });
      requestAnimationFrame(() => drawContent());
    } else if (coords && coords.length > 1) {
      console.log("[MAP AUTO-FOCUS] BRANCH 2: Fitting to GPS coordinates");
      // Create a bounding box from the coordinates
      const bounds = new mapboxgl.LngLatBounds();
      // Extend the bounds to include each coordinate

      coords.forEach((coord) => {
        bounds.extend([coord.lng, coord.lat]);
      });

      //Extend the bounds by 10% to give some padding.
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      const latDiff = ne.lat - sw.lat;
      const lngDiff = ne.lng - sw.lng;

      // Expand by 10%
      const latExpansion = latDiff * 0.1;
      const lngExpansion = lngDiff * 0.1;

      const newSw = new mapboxgl.LngLat(
        sw.lng - lngExpansion,
        sw.lat - latExpansion,
      );
      const newNe = new mapboxgl.LngLat(
        ne.lng + lngExpansion,
        ne.lat + latExpansion,
      );

      bounds.extend(newSw);
      bounds.extend(newNe);

      // Calculate padding that pushes GPS content away from any gallery overlay
      const padding = calculateGalleryAwarePadding(
        project.gallery,
        mapContainer.current?.offsetWidth ?? 0,
        mapContainer.current?.offsetHeight ?? 0,
      );
      map.current.fitBounds(bounds, { padding, duration: 3000 });

      // Clear the pending GPS fit flag - we've now fit to GPS coordinates
      // The moveend from this fitBounds will save the correct GPS-focused bounds
      if (pendingInitialGPSFitRef.current) {
        console.log("[MAP AUTO-FOCUS] Clearing pending GPS fit flag");
        pendingInitialGPSFitRef.current = false;
      }
      clearStyledPointsRequiringBoundsUpdate();
    } else if (coords && coords.length === 1) {
      // If there's only one coordinate, just center the map on it
      map.current.setCenter([coords[0].lng, coords[0].lat]);
      map.current.setZoom(15); // Or whatever zoom level you prefer for a single point
    }
  }, [gpsSource?.allCoordinates, project.styledPoints.length]);

  const increment = useMemo(
    () => incrementSizeInPX(project),
    [project.sizeOption],
  );

  // Ref for synchronous access to element under cursor (needed for touch handlers)
  const elementUnderCursorRef = useRef<MapElement | undefined>(undefined);

  // Draw content and compute element under cursor.
  // When pointerLocationOverride is provided, uses that instead of state (for synchronous touch handling).
  // Returns the computed element for immediate use.
  const drawContent = (pointerLocationOverride?: Point): MapElement | undefined => {
    if (mapContainer.current === undefined || mapContainer.current === null) {
      return undefined;
    }

    const effectivePointerLocation = pointerLocationOverride ?? pointerLocation;

    const drawingConfig: MapDrawingConfig = {
      map,
      canvasElement: canvasOverlay,
      originalMapSize: {
        width: mapContainer.current.offsetWidth,
        height: mapContainer.current.offsetHeight,
      },
      scale: 1,
      project,
      gpsSource,
      dotGridSpacing: isDragging ? increment : undefined,
      pointerLocation: effectivePointerLocation,
      pointerInteruptRadius: 50,
      elementToHighlight: undefined,
      selectedElement: editorUIState.editingElement,
      isRendering: false,
      projectEditingPermissions: project.editingPermissions,
      isTemplateEditing: isTemplateEditing,
      imageCache: imageCache,
      selectedViewportId: editorUIState.selectedViewportId,
    };

    const drawResult = drawMapElements(drawingConfig);

    const elementUnderCursor = drawResult.elementUnderPointerLocation;
    setSnapLines({
      vertical: drawResult.snapLines.vertical,
      horizontal: drawResult.snapLines.horizontal,
    });

    const width: number = canvasOverlay.current.width;
    const height: number = canvasOverlay.current.height;
    setMapSizeInPX({ width, height });

    const bounds = map.current.getBounds();
    if (!bounds) {
      return elementUnderCursor;
    }
    //map.current.dragPan.disable();

    if (map) {
      const type = elementUnderCursor?.type;
      if (
        type === "mapLabel" ||
        type === "staticLabel" ||
        type === "backdrop" ||
        isPanningViewport
      ) {
        map.current.dragPan.disable();
      } else {
        map.current.dragPan.enable();
      }
    }
    // Update both ref (synchronous) and state (async) for element under cursor
    elementUnderCursorRef.current = elementUnderCursor;
    setElementUnderCursor(elementUnderCursor);
    props.onChangeElementUnderCursor(elementUnderCursor);

    return elementUnderCursor;
  };

  // DRAW CONTENT ON MAP.
  useEffect(() => {
    if (map) {
      requestAnimationFrame(() => drawContent());
      const drawWithAnimationFrame = () => {
        // setEditingElement(undefined);
        requestAnimationFrame(() => drawContent());
      };

      map.current.on("move", drawWithAnimationFrame);
      map.current.on("zoom", drawWithAnimationFrame);
      map.current.on("idle", drawWithAnimationFrame);

      return () => {
        if (map.current) {
          map.current.off("move", drawWithAnimationFrame);
          map.current.off("zoom", drawWithAnimationFrame);
          map.current.off("idle", drawWithAnimationFrame);
        }
      };
    }
  }, [
    map,
    gpsSource?.displayedCoordinates,
    pointerLocation,
    project.lineStyle,
    project.styleUpdateKey,
    project.mapLabels,
    project.labelUpdateKey,
    editorUIState.editingElement,
    editorUIState.selectedViewportId,
    project.backdrops,
    project.backdropUpdateKey,
    project.gallery,
    project.galleryUpdateKey,
    project.sizeOption,
    project.mapStyle, // Re-attach event listeners when map style changes (map is recreated)
    imageCache,
    isPanningViewport,
    // editingElement,
  ]);

  useEffect(() => {
    if (!map.current || !mapContainer.current) return;

    map.current.resize();
    setMapRef(map);

    const handleIdle = () => {
      requestAnimationFrame(() => {
        if (canvasOverlay.current && mapContainer.current) {
          canvasOverlay.current.width = mapContainer.current.offsetWidth;
          canvasOverlay.current.height = mapContainer.current.offsetHeight;
        }

        drawContent();
      });

      map.current.off("idle", handleIdle);
    };

    map.current.on("idle", handleIdle);
  }, [project.sizeOption]);
  // const drawStaticMapCanvasOverlay = () => {
  //   if (map) {
  //     drawSmoothedLineWithParallelLines({
  //       map,
  //       coordinates: project.gpsSources[0].displayedCoordinates,
  //       targetRef: staticCanvasOverlay,
  //       originalMapWidth: mapContainer.current.offsetWidth,
  //       originalMapHeight: mapContainer.current.offsetHeight,
  //     });
  //   }
  // };
  const onClick = (_event: MouseEvent<HTMLDivElement>) => {
    // Use ref for synchronous access (needed for touch events where state may be stale)
    const element = elementUnderCursorRef.current;
    if (!element) {
      setDraggingElement(undefined);
    } else {
      setDraggingElement(element);
    }
  };

  if (sizePresetsLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div
      className={styles.mapContainer}
      onClick={props.onClick}
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <div
        ref={mapContainer}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "3px",
          pointerEvents: "auto",
        }}
        onClick={onClick}
        onMouseDown={() => handleMouseDown()}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onContextMenu={(e) => {
          // Prevent iOS context menu on long-press when touching elements
          if (elementUnderCursorRef.current) {
            e.preventDefault();
          }
        }}
      />
      <canvas
        ref={canvasOverlay}
        className={styles.canvasOverlay}
        width={mapContainer.current?.offsetWidth}
        height={mapContainer.current?.offsetHeight}
      />
      <canvas
        // ref={canvasOverlay}
        className={styles.canvasOverlay2}
        width={mapContainer.current?.offsetWidth}
        height={mapContainer.current?.offsetHeight}
      />
      <div
        className={styles.uiOverlay}
        style={{ aspectRatio: cssAspectRatio(project) }}
      >
        <MapUIOverlay
          pointerLocation={pointerLocation}
          isDragging={isDragging}
          map={map}
          elementUnderCursor={elementUnderCursor}
          onShowElementEditor={(element) => {
            // setEditingElement(element);
          }}
          isMobile={props.isMobile}
          onExposeViewportUploadTrigger={(trigger) => {
            viewportUploadTriggerRef.current = trigger;
          }}
        />
      </div>
      {/* Show focused viewport outline for touch interactions */}
      {editorUIState.focusedViewportRect && (
        <div
          style={{
            position: "absolute",
            left: editorUIState.focusedViewportRect.x,
            top: editorUIState.focusedViewportRect.y,
            width: editorUIState.focusedViewportRect.width,
            height: editorUIState.focusedViewportRect.height,
            border: "3px solid var(--color-interactive)",
            pointerEvents: "none",
            zIndex: 9999,
            boxSizing: "border-box",
          }}
        />
      )}
    </div>
  );
};
